const resolvedUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000/api';
console.log('🔗 [authService] Using relative API path:', resolvedUrl);
export const BACKEND_URL = resolvedUrl;

export interface User {
  id: number;
  wallet_address: string;
  username?: string;
  email?: string;
  avatar?: string;
  bio?: string;
  total_trades: number;
  total_volume: number;
  favorite_tokens: string[];
  is_active: boolean;
  last_login: string;
  created_at: string;
}

export interface LoginResponse {
  success: boolean;
  user: User;
  sessionToken: string;
}

export interface CandlestickPoint {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  buyVolume?: number;
  sellVolume?: number;
  timestamp?: number;
  direction?: 'buy' | 'sell' | 'neutral';
}

export const authService = {
  // User authentication
  async loginUser(walletAddress: string, profileData: any = {}): Promise<LoginResponse | null> {
    try {
      const response = await fetch(`${BACKEND_URL}/auth/user/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress,
          profileData: {
            username: profileData.username || '',
            avatar: profileData.avatar || '',
            bio: profileData.bio || ''
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Login error:', error);
      return null;
    }
  },

  // User logout
  async logoutUser(sessionToken: string): Promise<void> {
    try {
      await fetch(`${BACKEND_URL}/auth/user/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  // Update user profile
  async updateProfile(sessionToken: string, profileData: any): Promise<User | null> {
    try {
      const response = await fetch(`${BACKEND_URL}/auth/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`,
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        throw new Error('Profile update failed');
      }

      const result = await response.json();
      return result.success ? result.user : null;
    } catch (error) {
      console.error('Profile update error:', error);
      return null;
    }
  },

  // Submit trade (buy/sell) to backend trading service
  async submitTrade(tradeData: any): Promise<any | null> {
    try {
      const response = await fetch(`${BACKEND_URL}/trading`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tradeData),
      });

      if (!response.ok) {
        console.error('Failed to submit trade:', await response.text());
        return null;
      }

      const result = await response.json();
      return result.success ? result.transaction : null;
    } catch (error) {
      console.error('Error submitting trade:', error);
      return null;
    }
  },

  // Load trading history from backend
  async loadTradingHistory(identifier: string | number, sessionToken?: string, wallet?: string): Promise<{ items: any[]; meta: any | null; }> {
    if (!identifier) {
      return { items: [], meta: null };
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }

    try {
      // First, try to get the token ID if we have a symbol
      let tokenId = null;
      if (typeof identifier === 'string' && !identifier.startsWith('0x')) {
        // This is likely a symbol, try to find the token ID
        const tokensResponse = await fetch(`${BACKEND_URL}/tokens`);
        if (tokensResponse.ok) {
          const tokens = await tokensResponse.json();
          const token = tokens.find((t: any) => t.symbol.toLowerCase() === identifier.toLowerCase());
          if (token) {
            tokenId = token.id;
          }
        }
      } else if (typeof identifier === 'number' || (typeof identifier === 'string' && /^\d+$/.test(identifier))) {
        // This is already a token ID
        tokenId = parseInt(identifier.toString());
      }

      if (tokenId) {
        // Use the working recent endpoint with tokenId
        const params = new URLSearchParams();
        if (wallet) {
          params.set('wallet', wallet);
        }

        const response = await fetch(`${BACKEND_URL}/trading/recent?tokenId=${tokenId}&${params.toString()}`, {
          headers,
        });

        if (!response.ok) {
          console.warn('Failed to load trading history from backend');
          return { items: [], meta: null };
        }

        const result = await response.json();
        if (result.success && result.trades) {
          const tokenSymbol = result.trades.length > 0 ? result.trades[0].token_symbol : 'UNKNOWN';
          const items = result.trades.map((tx: any) => ({
            id: tx.id,
            user: tx.username || `${tx.user_wallet.slice(0, 4)}...${tx.user_wallet.slice(-4)}`,
            avatar: tx.username ? tx.username.slice(0, 1).toUpperCase() : tx.user_wallet.slice(2, 3).toUpperCase(),
            type: tx.transaction_type,
            amount: `${tx.sui_amount} SUI`,
            tokens: `${tx.token_amount} ${tx.token_symbol}`,
            price: `$${tx.price_per_token ? tx.price_per_token.toFixed(8) : '0.00000000'}`,
            timestamp: new Date(tx.created_at).getTime(),
            txHash: tx.transaction_hash
          }));
          return {
            items,
            meta: { tokenId, tokenSymbol },
          };
        }
      }

      return { items: [], meta: null };
    } catch (error) {
      console.error('Error loading trading history from backend:', error);
      return { items: [], meta: null };
    }
  },

  async fetchCandles(identifier: string | number | null | undefined, timeframe: string, limit = 120): Promise<{ candles: CandlestickPoint[]; timeframe: string; resolutionLabel: string; limit: number; }> {
    if (identifier === null || identifier === undefined) {
      return { candles: [], timeframe, resolutionLabel: '', limit };
    }

    const resolveNumericIdentifier = async (candidate: string | number): Promise<number | null> => {
      if (typeof candidate === 'number') {
        return Number.isFinite(candidate) ? candidate : null;
      }

      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (/^\d+$/.test(trimmed)) {
          return Number.parseInt(trimmed, 10);
        }

        if (!trimmed.startsWith('0x')) {
          try {
            const tokensResponse = await fetch(`${BACKEND_URL}/tokens`);
            if (tokensResponse.ok) {
              const tokens = await tokensResponse.json();
              const token = tokens.find((t: any) => String(t.symbol).toLowerCase() === trimmed.toLowerCase());
              if (token?.id !== undefined && token.id !== null) {
                return Number(token.id);
              }
            }
          } catch (err) {
            console.warn('Unable to resolve token symbol to id', err);
          }
        }
      }

      return null;
    };

    try {
      const numericId = await resolveNumericIdentifier(identifier);

      if (numericId === null) {
        return { candles: [], timeframe, resolutionLabel: '', limit };
      }

      const params = new URLSearchParams();
      params.set('timeframe', timeframe);
      params.set('limit', String(limit));

      const response = await fetch(`${BACKEND_URL}/trading/token/${numericId}/candles?${params.toString()}`);
      if (!response.ok) {
        console.warn('Failed to fetch candlestick data');
        return { candles: [], timeframe, resolutionLabel: '', limit };
      }

      const result = await response.json();
      if (!result.success) {
        return { candles: [], timeframe, resolutionLabel: '', limit };
      }

      return {
        candles: Array.isArray(result.candles) ? result.candles : [],
        timeframe: result.meta?.timeframe ?? timeframe,
        resolutionLabel: result.meta?.resolutionLabel ?? timeframe,
        limit: result.meta?.limit ?? limit,
      };
    } catch (error) {
      console.error('Error fetching candlestick data:', error);
      return { candles: [], timeframe, resolutionLabel: '', limit };
    }
  }
}
  ;

export default authService;
